import { Button, PageHeader, Descriptions, Card, Table, Spin, InputNumber, message, Modal, Input, Upload } from 'antd'
import { PlusOutlined } from '@ant-design/icons'
import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery } from 'react-query'

import Error from '../../components/Error'
import { getProductStockById, updateProductStock } from '../../network/ProductStock'
import { createUrlWithFiltersFromCurrentURL } from '../../utils/filterUtils'
import { upload } from '../../network/API'
import { useAuth } from '../../hooks/useAuth'

const UPLOAD_URI = '/files/fixed-sku-stock-history'

const ManageProductStock = ({ history }) => {
  const { id } = useParams()
  const { user } = useAuth()
  const [addRemove, setAddRemove] = useState({}) // { rowIndex: { add: number, remove: number } }
  const [applyModalVisible, setApplyModalVisible] = useState(false)
  const [applyComment, setApplyComment] = useState('')
  const [applyFileList, setApplyFileList] = useState([])
  const [applySubmitting, setApplySubmitting] = useState(false)

  const { data, isError, isLoading, refetch } = useQuery(
    ['getProductStockById', { id }],
    getProductStockById,
    { refetchOnWindowFocus: false, enabled: !!id }
  )

  useEffect(() => {
    if (data?.stock && Array.isArray(data.stock)) {
      setAddRemove(
        data.stock.reduce((acc, _, idx) => {
          acc[idx] = { add: undefined, remove: undefined }
          return acc
        }, {})
      )
    }
  }, [data?.stock])

  if (isError) {
    return (
      <>
        <PageHeader
          className="site-page-header"
          title="Manage stock"
          onBack={() => history.push(createUrlWithFiltersFromCurrentURL('/fixed-sku-stock'))}
        />
        <Error retry={refetch} />
      </>
    )
  }

  if (isLoading || !data) {
    return (
      <>
        <PageHeader
          className="site-page-header"
          title="Manage stock"
          onBack={() => history.push(createUrlWithFiltersFromCurrentURL('/fixed-sku-stock'))}
        />
        <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
          <Spin size="large" />
        </div>
      </>
    )
  }

  const fixedSku = data.fixedSku || {}
  const store = data.store || {}
  const stock = Array.isArray(data.stock) ? data.stock : []
  const storeName =
    typeof store.name === 'string' ? store.name : (store.name?.en || store.name?.zh || '–')

  const handleAddRemoveChange = (rowIndex, field, value) => {
    setAddRemove((prev) => ({
      ...prev,
      [rowIndex]: { ...(prev[rowIndex] || {}), [field]: value },
    }))
  }

  const buildNewStock = () =>
    stock.map((row, idx) => {
      const current = Number(row.stock) || 0
      const add = Number(addRemove[idx]?.add) || 0
      const remove = Number(addRemove[idx]?.remove) || 0
      const newValue = current + add - remove
      return {
        size_id: row.size_id,
        size_name_en: row.size_name_en,
        size_name_zh: row.size_name_zh,
        stock: newValue,
      }
    })

  const openApplyModal = () => {
    setApplyComment('')
    setApplyFileList([])
    setApplyModalVisible(true)
  }

  const closeApplyModal = () => {
    if (!applySubmitting) {
      setApplyModalVisible(false)
      setApplyComment('')
      setApplyFileList([])
    }
  }

  const handleApplyConfirm = () => {
    const newStock = buildNewStock()
    const storeId = data.store?.id ?? data.store
    const fixedSkuId = data.fixedSku?.id ?? data.fixedSku
    const amounts = stock.map((row, idx) => ({
      size_id: row.size_id,
      size_name_en: row.size_name_en,
      size_name_zh: row.size_name_zh,
      add: Number(addRemove[idx]?.add) || 0,
      removed: Number(addRemove[idx]?.remove) || 0,
    }))
    const payload = {
      store: storeId,
      fixedSku: fixedSkuId,
      stock: newStock,
      amounts,
      ...(user?.id != null ? { user: user.id } : {}),
      ...(applyComment.trim() ? { comment: applyComment.trim() } : {}),
    }

    const filesToUpload = applyFileList.filter((f) => f.originFileObj && !f.response)
    if (filesToUpload.length === 0) {
      const images = applyFileList.map((f) => f.response).filter(Boolean)
      if (images.length) payload.images = images
      setApplySubmitting(true)
      updateProductStock(payload, id)
        .then(() => {
          message.success('Stock updated')
          refetch()
          setAddRemove(
            stock.reduce((acc, _, idx) => {
              acc[idx] = { add: undefined, remove: undefined }
              return acc
            }, {})
          )
          closeApplyModal()
        })
        .catch(() => message.error('Failed to update stock'))
        .finally(() => setApplySubmitting(false))
      return
    }

    setApplySubmitting(true)
    Promise.all(
      filesToUpload.map((f) => upload(f.originFileObj, UPLOAD_URI))
    )
      .then((responses) => {
        const images = [
          ...applyFileList.filter((f) => f.response).map((f) => f.response),
          ...responses,
        ]
        if (images.length) payload.images = images
        return updateProductStock(payload, id)
      })
      .then(() => {
        message.success('Stock updated')
        refetch()
        setAddRemove(
          stock.reduce((acc, _, idx) => {
            acc[idx] = { add: undefined, remove: undefined }
            return acc
          }, {})
        )
        closeApplyModal()
      })
      .catch(() => message.error('Failed to update stock'))
      .finally(() => setApplySubmitting(false))
  }

  const applyUploadProps = {
    multiple: true,
    fileList: applyFileList,
    onChange: ({ fileList }) => setApplyFileList(fileList),
    beforeUpload: () => false,
  }

  const stockColumns = [
    { title: 'Size (EN)', dataIndex: 'size_name_en', key: 'size_name_en', render: (v) => v || '–', width: 100 },
    { title: 'Size (中文)', dataIndex: 'size_name_zh', key: 'size_name_zh', render: (v) => v || '–', width: 100 },
    { title: 'Current stock', dataIndex: 'stock', key: 'stock', render: (v) => v ?? '–', width: 100 },
    {
      title: 'Add',
      key: 'add',
      width: 120,
      render: (_, record, rowIndex) => (
        <InputNumber
          min={0}
          placeholder="0"
          value={addRemove[rowIndex]?.add}
          onChange={(val) => handleAddRemoveChange(rowIndex, 'add', val)}
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: 'Remove',
      key: 'remove',
      width: 120,
      render: (_, record, rowIndex) => (
        <InputNumber
          min={0}
          placeholder="0"
          value={addRemove[rowIndex]?.remove}
          onChange={(val) => handleAddRemoveChange(rowIndex, 'remove', val)}
          style={{ width: '100%' }}
        />
      ),
    },
  ]

  return (
    <>
      <PageHeader
        className="site-page-header"
        title="Manage stock"
        onBack={() => history.push(createUrlWithFiltersFromCurrentURL('/fixed-sku-stock'))}
        style={{ backgroundColor: '#fff' }}
        extra={[
          <Button key="view" onClick={() => history.push(createUrlWithFiltersFromCurrentURL(`/fixed-sku-stock/view/${id}`))}>
            View
          </Button>,
          <Button key="history" onClick={() => history.push(createUrlWithFiltersFromCurrentURL(`/fixed-sku-stock/history/${id}`))}>
            History
          </Button>,
          <Button key="apply" type="primary" onClick={openApplyModal} disabled={stock.length === 0}>
            Apply changes
          </Button>,
        ]}
      />
      <Modal
        title="Confirm stock update"
        visible={applyModalVisible}
        onCancel={closeApplyModal}
        onOk={handleApplyConfirm}
        okText="Apply"
        confirmLoading={applySubmitting}
        destroyOnClose
        width={520}
      >
        <p style={{ marginBottom: 16 }}>Apply the add/remove changes to stock. You can optionally add a comment and images.</p>
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', marginBottom: 6 }}>Comment (optional)</label>
          <Input.TextArea
            rows={3}
            placeholder="e.g. Invoice #123, return from customer"
            value={applyComment}
            onChange={(e) => setApplyComment(e.target.value)}
          />
        </div>
        <div>
          <label style={{ display: 'block', marginBottom: 6 }}>Images (optional)</label>
          <Upload {...applyUploadProps} listType="picture-card">
            <div>
              <PlusOutlined />
              <div style={{ marginTop: 8 }}>Upload</div>
            </div>
          </Upload>
        </div>
      </Modal>
      <div style={{ padding: 24, backgroundColor: '#f0f2f5', minHeight: 'calc(100vh - 64px)' }}>
        <div style={{ margin: '0 auto' }}>
          <Card title="Details" style={{ marginBottom: 24 }}>
            <Descriptions bordered column={1} size="small">
              <Descriptions.Item label="Fix SKU">{fixedSku.sku ?? '–'}</Descriptions.Item>
              <Descriptions.Item label="Fix SKU name">
                {fixedSku.name?.en ?? fixedSku.name?.zh ?? '–'}
              </Descriptions.Item>
              <Descriptions.Item label="Store">{storeName}</Descriptions.Item>
            </Descriptions>
          </Card>
          <Card title="Stock by size – add or remove quantity">
            {stock.length === 0 ? (
              <div style={{ color: '#999' }}>No stock entries. Edit the product stock to add sizes.</div>
            ) : (
              <>
                <Table
                  dataSource={stock.map((row, idx) => ({ ...row, key: idx }))}
                  columns={stockColumns}
                  pagination={false}
                  size="small"
                  style={{ marginBottom: 16 }}
                />
                <Button type="primary" onClick={openApplyModal}>
                  Apply changes
                </Button>
              </>
            )}
          </Card>
        </div>
      </div>
    </>
  )
}

export default ManageProductStock
